import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import {
  Target,
  TrendingUp,
  Calendar,
  ChevronRight,
  Clock,
  BarChart3,
  BookOpen,
  Award,
  AlertTriangle,
  Info,
  Settings,
  Sparkles,
  ArrowRight
} from 'lucide-react'
import { KPAData } from '@/types/evidence'

interface PerformanceOnePagerProps {
  employeeName: string
  kpas: KPAData[]
  lastReviewDate?: string
  nextReviewDate?: string
  reviewPeriod?: 'MIDYEAR' | 'YEAREND'
  developmentPlan?: {
    goals: string[]
    completedGoals: number
    totalGoals: number
  }
  alerts: string[]
  onNavigateToKPA: (kpaId: string) => void
  onNavigateToObjective: (objectiveId: number) => void
  onCreateObjective?: () => void
  onConfigureObjectives?: () => void
  userRole?: 'employee' | 'manager'
  /**
   * Simple ↔ Detailed presentation. Simple (default) hides RAG colors, At-Risk
   * pills, Avg Progress %, and per-objective progress badges to make the
   * page less metric-anxiety-driven. Detailed restores the full dashboard view.
   */
  journeyViewMode?: 'simple' | 'detailed'
}

// Roman numerals for the chapter marks on each KPA. Up to 12 covers the
// realistic max KPA count for a single role; falls back to digits.
const ROMAN_NUMERALS = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii']
const romanNumeral = (n: number): string => (n >= 1 && n <= ROMAN_NUMERALS.length ? ROMAN_NUMERALS[n - 1] : String(n))

export default function PerformanceOnePager({
  employeeName,
  kpas,
  lastReviewDate,
  nextReviewDate,
  reviewPeriod,
  developmentPlan,
  alerts = [],
  onNavigateToKPA,
  onNavigateToObjective,
  onCreateObjective,
  onConfigureObjectives,
  userRole: _userRole = 'employee',
  journeyViewMode = 'simple'
}: PerformanceOnePagerProps) {
  
  // Sort KPAs by sort_order to ensure consistent ordering
  // Order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
  const sortedKPAs = [...kpas].sort((a, b) => ((a as any).sort_order || 999) - ((b as any).sort_order || 999))
  
  // Calculate individual objective progress from KPIs
  // Respects measurement_direction (matches kpiProgress.ts calculateProgress)
  const calculateObjectiveProgress = (objective: any) => {
    if (!objective.kpis || objective.kpis.length === 0) {
      return objective.progress || 0
    }
    
    const totalProgress = objective.kpis.reduce((sum: number, kpi: any) => {
      const target = kpi.target_value
      const current = kpi.current_value ?? null
      const direction = kpi.measurement_direction || 'higher_is_better'

      if (current === null || current === undefined || !target || target === 0) return sum

      let kpiProgress: number
      if (direction === 'lower_is_better') {
        // current=0 means no data recorded yet
        if (current === 0) { kpiProgress = 0 }
        else { kpiProgress = Math.min((target / current) * 100, 100) }
      } else {
        kpiProgress = Math.min((current / target) * 100, 100)
      }

      return sum + kpiProgress
    }, 0)
    
    return Math.round(totalProgress / objective.kpis.length)
  }
  
  // Calculate dynamic KPA progress from objectives (same logic as EmployeeKPAView)
  const calculateKPAProgress = (kpa: KPAData) => {
    if (!kpa.individualObjectives || kpa.individualObjectives.length === 0) {
      return kpa.averageProgress || 0
    }
    
    // Calculate each objective's progress using the helper function
    const totalProgress = kpa.individualObjectives.reduce((sum, objective) => {
      return sum + calculateObjectiveProgress(objective)
    }, 0)
    
    return Math.round(totalProgress / kpa.individualObjectives.length)
  }
  
  // Calculate overall metrics using sortedKPAs
  const totalObjectives = sortedKPAs.reduce((sum, kpa) => sum + kpa.individualObjectives.length, 0)
  const totalKPIs = sortedKPAs.reduce((sum, kpa) => sum + (kpa.underlyingKPIs?.length || 0), 0)
  const avgProgress = sortedKPAs.length > 0
    ? Math.round(sortedKPAs.reduce((sum, kpa) => sum + calculateKPAProgress(kpa), 0) / sortedKPAs.length)
    : 0

  // ─────────────────────────────────────────────────────────────────────
  // The Quarterly — editorial Simple-view treatment.
  //
  // Rather than retro-fitting "hide chips" onto the metrics dashboard, the
  // Simple view gets a wholly different surface: warm bone background, a
  // serif headline + italic standfirst above a single-column reading list
  // of KPAs. No stat cards, no risk pills, no progress %. Each KPA is an
  // article preview the user clicks to read. Detailed mode (below) remains
  // the unchanged enterprise dashboard.
  // ─────────────────────────────────────────────────────────────────────
  if (journeyViewMode === 'simple') {
    // Dateline (magazine masthead). Built from real data, in priority order:
    //  1. an explicit nextReviewDate, if ever wired in → "Next reflection · <date>"
    //  2. the review point being viewed (reviewPeriod) + the cycle year, where
    //     the cycle year is the latest objective end_date across all KPAs
    //     → "Mid-Year Reflection · 2026" / "Year-End Reflection · 2026"
    //  3. just the cycle year, if the period is unknown → "Reflection · 2026"
    //  4. the original neutral fallback when there's no data at all.
    const cycleYear = (() => {
      const years = sortedKPAs
        .flatMap((k) => k.individualObjectives.map((o) => o.end_date))
        .filter(Boolean)
        .map((d) => new Date(d as string).getFullYear())
        .filter((y) => !Number.isNaN(y))
      return years.length ? Math.max(...years) : null
    })()
    const periodWord =
      reviewPeriod === 'MIDYEAR' ? 'Mid-Year Reflection'
      : reviewPeriod === 'YEAREND' ? 'Year-End Reflection'
      : null
    const reviewLabel = nextReviewDate
      ? `Next reflection · ${new Date(nextReviewDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()}`
      : periodWord
        ? `${periodWord}${cycleYear ? ` · ${cycleYear}` : ''}`
        : cycleYear
          ? `Reflection · ${cycleYear}`
          : 'Mid-Year & Year-End'

    return (
      <div className="bg-quarterly-bone -mx-3 sm:-mx-6 md:-mx-8 px-6 sm:px-10 md:px-16 lg:px-24 py-10 md:py-14 min-h-[calc(100vh-180px)]">
        <div className="max-w-[720px] mx-auto">
          {/* Dateline — small caps, tracked, like a magazine masthead */}
          <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-6">
            {reviewLabel}
          </p>

          {/* Configure Objectives CTA — quietly rendered as an inline note,
              not a boxy gradient panel. Tobacco accent reserved for the link. */}
          {onConfigureObjectives && (
            <button
              onClick={onConfigureObjectives}
              className="group block w-full text-left mb-10 pb-6 border-b border-quarterly-rule"
            >
              <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-tobacco uppercase mb-1">
                Begin
              </p>
              <p className="font-serif text-[20px] text-quarterly-ink group-hover:text-quarterly-tobacco transition-colors">
                Configure my objectives
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </p>
            </button>
          )}

          {/* The reading list — each KPA as an article preview. No cards,
              no boxes, no risk indicators. Hairline rules in between. */}
          <ol className="divide-y divide-quarterly-rule">
            {sortedKPAs.map((kpa, idx) => {
              const activeObjectives = kpa.individualObjectives.filter((o) => o.status !== 'rejected')
              const objectiveCount = activeObjectives.length
              const lastEntry = (() => {
                // Most recent objective end_date is a proxy for "last activity".
                // If we ever surface evidence on this view, swap to evidence date.
                const dates = activeObjectives
                  .map((o) => o.end_date)
                  .filter(Boolean) as string[]
                if (!dates.length) return null
                return dates.sort().reverse()[0]
              })()

              return (
                <li
                  key={kpa.id}
                  className="group cursor-pointer py-7 first:pt-0 transition-colors"
                  onClick={() => onNavigateToKPA(kpa.id)}
                >
                  {/* Roman numeral chapter mark, mono small caps */}
                  <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-2">
                    {romanNumeral(idx + 1)}
                  </p>

                  {/* KPA title — serif display, drives the eye */}
                  <h2 className="font-serif text-[28px] md:text-[32px] leading-[1.15] tracking-tight text-quarterly-ink mb-2 group-hover:text-quarterly-tobacco transition-colors">
                    {kpa.display_name || kpa.name}
                    <span className="inline-block ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-quarterly-tobacco">
                      →
                    </span>
                  </h2>

                  {/* Standfirst — italic Newsreader, the description */}
                  {kpa.description && (
                    <p className="font-editorial italic text-quarterly-ink-muted text-[17px] leading-[1.55] mb-3 max-w-[600px]">
                      {kpa.description}
                    </p>
                  )}

                  {/* The objectives in this area, each with its KPI count —
                      gives the reader the actual work, not just a tally. Quiet
                      serif list with a tobacco em-dash marker; no boxes. */}
                  {activeObjectives.length > 0 && (
                    <ul className="mt-1 mb-4 space-y-1.5">
                      {activeObjectives.map((obj) => {
                        const kpiCount = obj.kpis?.length || 0
                        return (
                          <li key={obj.id} className="flex gap-2.5 text-[15px] leading-[1.5] text-quarterly-ink">
                            <span className="text-quarterly-tobacco/50 select-none" aria-hidden="true">—</span>
                            <span>
                              {obj.title}
                              {kpiCount > 0 && (
                                <span className="text-quarterly-ink-muted">{` · ${kpiCount} ${kpiCount === 1 ? 'KPI' : 'KPIs'}`}</span>
                              )}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  {/* Mono meta — neutral, descriptive facts only */}
                  <p className="text-[11px] font-medium tracking-[0.18em] text-quarterly-ink-muted uppercase">
                    {objectiveCount} {objectiveCount === 1 ? 'objective' : 'objectives'}
                    {lastEntry && (
                      <>
                        <span className="mx-2">·</span>
                        Through {new Date(lastEntry).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </>
                    )}
                  </p>
                </li>
              )
            })}
          </ol>

          {/* Footer — mono small caps, like a colophon */}
          <p className="mt-14 pt-6 border-t border-quarterly-rule text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase text-center">
            Private to you · Switch to Detailed for the full picture
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Configure Objectives CTA - Prominent Position */}
      {onConfigureObjectives && (
        <Card className="border-2 border-dashed border-heritage-blue/30 bg-gradient-to-r from-heritage-blue/5 via-future-blue/5 to-heritage-blue/5 hover:border-heritage-blue/50 hover:shadow-lg transition-all group cursor-pointer" onClick={onConfigureObjectives}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-heritage-blue to-future-blue flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Settings className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg text-heritage-blue">Configure My Objectives</h3>
                    <Badge className="bg-future-blue/10 text-future-blue border-future-blue/20">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Setup
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Set up or update your objectives for this cycle with your people leader
                  </p>
                </div>
              </div>
              <Button className="bg-heritage-blue hover:bg-heritage-blue/90 text-white gap-2 shadow-md group-hover:shadow-lg transition-all">
                Get Started
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Metrics Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-heritage-blue/20 bg-gradient-to-br from-heritage-blue/5 to-heritage-blue/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total KPAs</p>
                <p className="text-3xl font-bold text-heritage-blue mt-1">{sortedKPAs.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-heritage-blue/10 flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-heritage-blue" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Objectives</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{totalObjectives}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total KPIs</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">{totalKPIs}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {journeyViewMode === 'detailed' ? (
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Avg Progress</p>
                  <p className="text-3xl font-bold text-green-700 mt-1">{avgProgress}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-stone-200 bg-stone-50/60">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Reflection cadence</p>
                  <p className="text-base font-semibold text-stone-800 mt-1 leading-tight">
                    {nextReviewDate
                      ? `Next: ${new Date(nextReviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                      : 'Mid-Year & Year-End'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-stone-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alerts & Action Items */}
      {alerts.length > 0 && (
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-2">Action Required ({alerts.length})</h3>
                <ul className="space-y-1">
                  {alerts.slice(0, 5).map((alert, i) => (
                    <li key={i} className="text-sm text-red-800 flex items-start gap-2">
                      <span className="text-red-600 mt-0.5">•</span>
                      <span>{alert}</span>
                    </li>
                  ))}
                </ul>
                {alerts.length > 5 && (
                  <p className="text-xs text-red-700 mt-2">+ {alerts.length - 5} more items</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPAs Grid */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Your Key Performance Areas</h2>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sortedKPAs.map((kpa) => {
          const progress = calculateKPAProgress(kpa)

          return (
            <Card
              key={kpa.id}
              className="group cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]"
              onClick={() => onNavigateToKPA(kpa.id)}
            >
              <CardContent className="p-5">
                {/* KPA Header — donut + % only in Detailed; Simple shows a
                    neutral badge so KPAs don't read as scored. */}
                <div className="flex items-start gap-4 mb-4">
                  {journeyViewMode === 'detailed' ? (
                    <div className="relative flex-shrink-0">
                      <svg className="w-16 h-16 transform -rotate-90">
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          className="text-gray-200"
                        />
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 28}`}
                          strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                          className="transition-all text-heritage-blue"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-bold text-gray-900">{progress}%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="relative flex-shrink-0 w-16 h-16 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center">
                      <Target className="h-6 w-6 text-stone-500" />
                    </div>
                  )}
                  
                  {/* KPA Title and Badge */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-lg text-gray-900 group-hover:text-heritage-blue transition-colors leading-tight">
                        {kpa.display_name || kpa.name}
                      </h3>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-heritage-blue transition-all group-hover:translate-x-1 flex-shrink-0" />
                    </div>
                  </div>
                </div>
                
                {/* Stats Row — two compact number tiles; fit side-by-side
                    even at 375px since the parent KPAs grid stacks to one
                    column on mobile. */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                    <Target className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-600">Objectives</p>
                      <p className="text-lg font-bold text-blue-600">{kpa.individualObjectives.length}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
                    <BarChart3 className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-600">KPIs</p>
                      <p className="text-lg font-bold text-purple-600">{kpa.underlyingKPIs?.length || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Objectives Preview */}
                {kpa.individualObjectives.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Objectives ({kpa.individualObjectives.length})</p>
                    {kpa.individualObjectives.slice(0, 3).map((obj) => {
                      const objProgress = calculateObjectiveProgress(obj)
                      return (
                        <div
                          key={obj.id}
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
                          onClick={(e) => {
                            e.stopPropagation()
                            onNavigateToObjective(obj.id)
                          }}
                        >
                          <div className="flex-shrink-0">
                            <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center">
                              <Target className="h-3.5 w-3.5 text-stone-500" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{obj.title}</p>
                          </div>
                          {journeyViewMode === 'detailed' ? (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs font-bold text-gray-900">{objProgress}%</span>
                              <div className="w-12">
                                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full transition-all bg-heritage-blue"
                                    style={{ width: `${objProgress}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-stone-500 flex-shrink-0">In progress</span>
                          )}
                        </div>
                      )
                    })}
                    {kpa.individualObjectives.length > 3 && (
                      <p className="text-xs text-gray-500 text-center pt-1">
                        + {kpa.individualObjectives.length - 3} more objectives
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-gray-400 bg-gray-50 rounded-lg">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No objectives yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

    </div>
  )
}
